import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { ArrowRight } from 'lucide-react';
import { useWeb3 } from '../../contexts/Web3Context';

const InvestmentForm = forwardRef(({ minDeposit, tokenPrice, topupWallet: initialTopupWallet = '0', initialReferrerId = '', onInvest }, ref) => {
  const { userInfo } = useWeb3();
  const [investAmount, setInvestAmount] = useState('');
  const [referrerId, setReferrerId] = useState(initialReferrerId);
  const [useTopupWallet, setUseTopupWallet] = useState(false);
  const [tokenCashback, setTokenCashback] = useState('0');
  const [topupWallet, setTopupWallet] = useState(initialTopupWallet);
  
  // Expose reset method to parent component through ref
  useImperativeHandle(ref, () => ({
    resetForm: () => {
      setInvestAmount('');
      setUseTopupWallet(false);
      setTokenCashback('0');
      // Don't reset referrerId as it might have been set from URL
    }
  }));
  
  // Set topup wallet from user info
  useEffect(() => {
    if (userInfo && userInfo.topupWallet) {
      setTopupWallet(userInfo.topupWallet);
    }
  }, [userInfo]);
  
  // Set initial referrer ID if provided
  useEffect(() => {
    if (initialReferrerId) {
      setReferrerId(initialReferrerId);
    }
  }, [initialReferrerId]);

  // Calculate token cashback when investment amount changes
  useEffect(() => {
    if (investAmount && parseFloat(investAmount) > 0 && tokenPrice) {
      // Token cashback is 10% of investment
      const cashbackValue = (parseFloat(investAmount) * 10) / 100;
      const tokenAmount = cashbackValue / parseFloat(tokenPrice);
      setTokenCashback(tokenAmount.toFixed(2));
    } else {
      setTokenCashback('0');
    }
  }, [investAmount, tokenPrice]);

  // Handle investment amount change
  const handleAmountChange = (e) => {
    setInvestAmount(e.target.value);
  };

  // Handle referrer ID change
  const handleReferrerIdChange = (e) => {
    setReferrerId(e.target.value);
  };

  // Handle topup wallet checkbox change
  const handleTopupWalletChange = (e) => {
    const checked = e.target.checked;
    setUseTopupWallet(checked);
    
    if (checked && topupWallet) {
      // Use topup wallet amount as investment amount
      setInvestAmount(topupWallet);
    } else {
      // Clear investment amount if unchecking
      setInvestAmount('');
    }
  };

  // Handle invest button click
  const handleInvest = () => {
    // Validate input
    if (!investAmount || parseFloat(investAmount) <= 0 || parseFloat(investAmount) < parseFloat(minDeposit)) {
      return;
    }
    
    // Call the onInvest callback
    onInvest(investAmount, referrerId, useTopupWallet);
  };

  return (
    <div className="investment-form">
      <h2>Make an Investment</h2>
      
      <div className="form-group">
        <label htmlFor="investAmount">Investment Amount (USDT)</label>
        <div className="input-with-icon">
          <input 
            type="number" 
            id="investAmount"
            min={minDeposit}
            value={investAmount}
            onChange={handleAmountChange}
            placeholder={`Minimum ${minDeposit} USDT`}
            disabled={useTopupWallet}
          />
          <span className="input-icon">USDT</span>
        </div>
        <p className="min-note">Minimum investment: <span>{minDeposit}</span> USDT</p>
      </div>
      
      <div className="form-group">
        <label htmlFor="referrerId">Referrer ID (Optional)</label>
        <input 
          type="text" 
          id="referrerId"
          value={referrerId}
          onChange={handleReferrerIdChange}
          placeholder="Enter referrer ID if any"
        />
      </div>
      
      <div className="form-group checkbox-group">
        <input 
          type="checkbox" 
          id="useTopupWallet"
          checked={useTopupWallet}
          onChange={handleTopupWalletChange}
          disabled={!topupWallet || parseFloat(topupWallet) <= 0}
        />
        <label htmlFor="useTopupWallet">Use Topup Wallet Balance</label>
        <span id="topupBalance">Available: {topupWallet} USDT</span>
      </div>
      
      <div className="investment-summary">
        <div className="summary-item">
          <span>Token Cashback (10%)</span>
          <span id="tokenCashback">{tokenCashback} CRYPTO</span>
        </div>
        <div className="summary-item">
          <span>Current Price</span>
          <span id="tokenPrice">{tokenPrice} USDT</span>
        </div>
      </div>
      
      <button 
        className="invest-btn" 
        onClick={handleInvest}
        disabled={!investAmount || parseFloat(investAmount) <= 0 || parseFloat(investAmount) < parseFloat(minDeposit)}
      >
        <span className="btn-text">Invest Now</span>
        <span className="btn-icon"><ArrowRight size={16} /></span>
      </button>
    </div>
  );
});

export default InvestmentForm;